import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaSlack, FaCheck, FaTimes, FaCog, FaHashtag } from 'react-icons/fa';
import { useAuth } from '../utils/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const SlackIntegration = () => {
  const { token: authToken } = useAuth();
  const [status, setStatus] = useState(null);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [settings, setSettings] = useState({
    defaultChannel: '',
    autoPost: true,
    notifyActionItems: true
  });

  // Get token from context or localStorage as fallback
  const getToken = () => authToken || localStorage.getItem('token');

  // Check for OAuth callback result in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const slackResult = params.get('slack');
    const teamName = params.get('team');
    const message = params.get('message');

    if (slackResult === 'success') {
      setSuccess(`Successfully connected to ${teamName || 'Slack'}!`);
      // Clean up URL and refetch status
      window.history.replaceState({}, document.title, window.location.pathname);
      // Force refetch status after successful OAuth
      setTimeout(() => {
        setLoading(true);
        fetchStatus();
      }, 500);
    } else if (slackResult === 'error') {
      setError(`Failed to connect: ${message || 'Unknown error'}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch Slack status
  const fetchStatus = useCallback(async () => {
    const currentToken = getToken();
    
    if (!currentToken) {
      setLoading(false);
      setError('Please log in to manage integrations');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/integrations/slack/status`, {
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Slack status response:', data);
      setStatus(data);
      
      if (data.connected) {
        console.log('Slack is connected, fetching channels...');
        setSettings({
          defaultChannel: data.defaultChannel || '',
          autoPost: data.autoPost ?? true,
          notifyActionItems: data.notifyActionItems ?? true
        });
      }
    } catch (err) {
      console.error('Error fetching Slack status:', err);
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // Fetch channels if connected
  const fetchChannels = useCallback(async () => {
    const currentToken = getToken();
    if (!currentToken) return;
    
    try {
      const response = await fetch(`${API_URL}/api/integrations/slack/channels`, {
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setChannels(data.channels);
      }
    } catch (err) {
      console.error('Error fetching channels:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => {
    // Fetch status when component mounts
    const currentToken = getToken();
    console.log('SlackIntegration mounted, token exists:', !!currentToken);
    if (currentToken) {
      fetchStatus();
    } else {
      // Wait a bit for token to be available from localStorage
      const timer = setTimeout(() => {
        const retryToken = getToken();
        console.log('Retry token check:', !!retryToken);
        if (retryToken) {
          fetchStatus();
        } else {
          setLoading(false);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status?.connected) {
      fetchChannels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.connected]);

  // Connect to Slack
  const handleConnect = async () => {
    const currentToken = getToken();
    console.log('Slack connect - token exists:', !!currentToken, 'length:', currentToken?.length);
    
    if (!currentToken) {
      setError('Please log in to connect Slack');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/integrations/slack/auth`, {
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });
      
      if (!response.ok) {
        console.error('Slack auth failed:', response.status, response.statusText);
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || `Authentication failed (${response.status})`);
        return;
      }
      
      const data = await response.json();
      
      if (data.authUrl) {
        console.log('Redirecting to Slack OAuth:', data.authUrl);
        window.location.href = data.authUrl;
      } else {
        setError('No auth URL received from server');
      }
    } catch (err) {
      console.error('Slack connect error:', err);
      setError('Failed to initiate Slack connection');
    }
  };

  // Disconnect from Slack
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Slack?')) return;
    
    const currentToken = getToken();
    try {
      const response = await fetch(`${API_URL}/api/integrations/slack/disconnect`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });
      
      if (response.ok) {
        setStatus({ connected: false });
        setSuccess('Slack disconnected successfully');
      }
    } catch (err) {
      setError('Failed to disconnect Slack');
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    setSaving(true);
    setError(null);
    
    const currentToken = getToken();
    try {
      const response = await fetch(`${API_URL}/api/integrations/slack/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Settings saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to save settings');
      }
    } catch (err) {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Send test message
  const handleTestMessage = async () => {
    setTesting(true);
    setError(null);
    
    const currentToken = getToken();
    try {
      const response = await fetch(`${API_URL}/api/integrations/slack/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Test message sent! Check your Slack channel.');
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.message || 'Failed to send test message');
      }
    } catch (err) {
      setError('Failed to send test message');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card className="mb-4">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading Slack integration...
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <FaSlack className="me-2" style={{ color: '#4A154B', fontSize: '1.5rem' }} />
          <span className="fw-bold">Slack Integration</span>
        </div>
        {status?.connected && (
          <Badge bg="success" className="d-flex align-items-center">
            <FaCheck className="me-1" /> Connected
          </Badge>
        )}
      </Card.Header>
      
      <Card.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {!status?.connected ? (
          // Not connected - show connect button
          <div className="text-center py-3">
            <p className="text-muted mb-3">
              Connect Slack to automatically post meeting summaries to your channels
              and get notified about action items.
            </p>
            <Button 
              variant="primary" 
              onClick={handleConnect}
              className="d-flex align-items-center mx-auto"
              style={{ backgroundColor: '#4A154B', borderColor: '#4A154B' }}
            >
              <FaSlack className="me-2" />
              Connect to Slack
            </Button>
          </div>
        ) : (
          // Connected - show settings
          <div>
            <div className="mb-3 p-3 bg-light rounded">
              <small className="text-muted">Connected to workspace:</small>
              <div className="fw-bold">{status.teamName}</div>
              <small className="text-muted">
                Connected {status.connectedAt ? new Date(status.connectedAt).toLocaleDateString() : 'recently'}
              </small>
            </div>

            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="d-flex align-items-center">
                  <FaHashtag className="me-2" />
                  Default Channel
                </Form.Label>
                <Form.Select
                  value={settings.defaultChannel}
                  onChange={(e) => setSettings({ ...settings, defaultChannel: e.target.value })}
                >
                  <option value="">Select a channel...</option>
                  {channels.map(channel => (
                    <option key={channel.id} value={channel.id}>
                      #{channel.name} {channel.isPrivate ? '🔒' : ''}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Meeting summaries will be posted to this channel
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="autoPost"
                  label="Automatically post meeting summaries"
                  checked={settings.autoPost}
                  onChange={(e) => setSettings({ ...settings, autoPost: e.target.checked })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="notifyActionItems"
                  label="Send DM notifications for action items"
                  checked={settings.notifyActionItems}
                  onChange={(e) => setSettings({ ...settings, notifyActionItems: e.target.checked })}
                />
              </Form.Group>

              <div className="d-flex gap-2 flex-wrap">
                <Button 
                  variant="primary" 
                  onClick={handleSaveSettings}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaCog className="me-2" />
                      Save Settings
                    </>
                  )}
                </Button>

                <Button 
                  variant="outline-primary" 
                  onClick={handleTestMessage}
                  disabled={testing || !settings.defaultChannel}
                >
                  {testing ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Test Message'
                  )}
                </Button>

                <Button 
                  variant="outline-danger" 
                  onClick={handleDisconnect}
                  className="ms-auto"
                >
                  <FaTimes className="me-2" />
                  Disconnect
                </Button>
              </div>
            </Form>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default SlackIntegration;
